import ApiService from './api';
import type { ApiResponse } from '~/types/api';

// Site key yanıt tipi
interface SiteKeyResponse {
  site_key: string;
}

// Token doğrulama yanıt tipi
interface VerifyResponse {
  success: boolean;
  score?: number;
  action?: string;
  challenge_ts?: string;
  hostname?: string;
}

/**
 * reCAPTCHA servisi - site key ve token doğrulama işlemleri için
 */
export class RecaptchaService {
  private static instance: RecaptchaService;
  private apiService: ApiService;
  private siteKey: string | null = null;
  private isLoading: boolean = false;
  private isError: boolean = false;
  private errorMessage: string = '';

  /**
   * Singleton pattern için private constructor
   */
  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  /**
   * Singleton instance döndür
   */
  public static getInstance(): RecaptchaService {
    if (!RecaptchaService.instance) {
      RecaptchaService.instance = new RecaptchaService();
    }
    return RecaptchaService.instance;
  }

  /**
   * Site key'i backend'den al
   * @returns site key
   */
  public async getSiteKey(): Promise<string | null> {
    // Daha önce yüklendiyse cache'den döndür
    if (this.siteKey) {
      return this.siteKey;
    }

    // Yükleme işlemi zaten devam ediyorsa bekle
    if (this.isLoading) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isLoading) {
            clearInterval(checkInterval);
            resolve(this.siteKey);
          }
        }, 100);
      });
    }

    // Yükleme işlemini başlat
    this.isLoading = true;
    this.isError = false;
    this.errorMessage = '';

    try {
      const response = await this.apiService.get<ApiResponse<any>>('/public/recaptcha/site-key');
      
      // API yanıtı kontrolü
      if (response && 
         (response.status === 'success' || response.status === 200 || response.status === true) && 
         response.data && 
         typeof response.data === 'object') {
        
        // API yanıtı doğrudan site_key içeriyorsa veya data nesnesi içinde site_key varsa
        if ('site_key' in response.data) {
          this.siteKey = String(response.data.site_key);
        } else if (response.data.data && 'site_key' in response.data.data) {
          this.siteKey = String(response.data.data.site_key);
        }
        
        if (this.siteKey) {
          console.info('reCAPTCHA site key başarıyla alındı');
          return this.siteKey;
        }
      }
      
      this.isError = true;
      this.errorMessage = response?.message || 'reCAPTCHA site key alınamadı';
      console.warn(this.errorMessage);
      return null;
    } catch (error) {
      this.isError = true;
      this.errorMessage = 'reCAPTCHA site key alınırken hata oluştu';
      console.error(this.errorMessage, error);
      return null;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * reCAPTCHA token'ı doğrula
   * @param token reCAPTCHA token
   * @param action işlem türü (login, contact_form, vs.)
   * @param minScore minimum kabul edilebilir skor (0.0-1.0 arası)
   * @returns doğrulama başarılı mı
   */
  public async verifyToken(token: string, action: string, minScore: number = 0.5): Promise<boolean> {
    try {
      const response = await this.apiService.post<ApiResponse<any>>('/public/recaptcha/verify', {
        token,
        action,
        min_score: minScore
      });
      
      // API yanıtı kontrolü
      if (response && 
         (response.status === 'success' || response.status === 200 || response.status === true) && 
         response.data) {
        
        // API yanıtı doğrudan success içeriyorsa
        if (response.data && typeof response.data === 'object' && 'success' in response.data) {
          return Boolean(response.data.success);
        } 
        // API yanıtı data nesnesi içinde success içeriyorsa
        else if (response.data && typeof response.data === 'object' && 
                 response.data.data && typeof response.data.data === 'object' && 
                 'success' in response.data.data) {
          return Boolean(response.data.data.success);
        }
      }
      
      return false;
    } catch (error) {
      console.error('reCAPTCHA token doğrulanırken hata oluştu:', error);
      return false;
    }
  }
} 